// =============================================================================
// Store: sucursal activa (hallazgo A2 resuelto)
// -----------------------------------------------------------------------------
// Centraliza el estado de sucursal (id activo + lista permitida + nombre) que
// antes se resolvía en MainLayout sobre SecureStorage. La persistencia del id
// activo sigue yendo al usuario en SecureStorage (misma clave 'frontline_user').
// =============================================================================
import { ref } from 'vue';
import { session } from '@/utils/session';
import { SecureStorage } from '@/utils/secureStorage';

const selectedBranchId = ref(null);
const userBranches = ref([]);
const currentBranchName = ref('Cargando agencia...');
const isLoadingBranches = ref(false);

// Versión del contexto de sucursal (RX-4): cada cambio incrementa el contador y
// las vistas suscritas (p. ej. App.vue con :key en <router-view>) se re-hidratan
// sin `window.location.reload()`.
const branchVersion = ref(0);

const parseBranchIds = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(Number);
  if (typeof raw === 'string' && raw.startsWith('[')) {
    try { return JSON.parse(raw).map(Number); } catch (e) { return []; }
  }
  return [Number(raw)];
};

export function useBranchStore() {
  /** Inicializa el estado a partir del usuario en sesión. */
  const init = () => {
    const user = session.getUser();
    if (!user) {
      selectedBranchId.value = null;
      userBranches.value = [];
      currentBranchName.value = 'Sin Sucursal';
      return undefined;
    }
    const allowed = parseBranchIds(user.allowed_branches);
    const uniqueAllowed = allowed.length > 0 ? allowed : parseBranchIds(user.branch);
    const active = Number(user.branch) || uniqueAllowed[0];
    selectedBranchId.value = active;
    currentBranchName.value = `Agencia #${active}`;
    return { allowedIds: uniqueAllowed, activeId: active, companyId: user.company };
  };

  /** Guarda la lista de sucursales (ya filtrada por los ids permitidos). */
  const setBranches = (list, allowedIds, activeId) => {
    userBranches.value = (list || []).filter((b) => allowedIds.includes(Number(b.id)));
    const active = userBranches.value.find((b) => Number(b.id) === activeId);
    currentBranchName.value = active ? active.name : `Agencia #${activeId}`;
  };

  /** Cambia la sucursal de trabajo y la persiste en el usuario de la sesión. */
  const setActiveBranch = (branchId) => {
    const user = session.getUser();
    if (!user) return;
    user.branch = Number(branchId);
    user.allowed_branches = parseBranchIds(user.allowed_branches);
    SecureStorage.setItem('frontline_user', user);
    selectedBranchId.value = Number(branchId);
  };

  /**
   * Sucursal activa como ESCALAR (Number), lista para usarse como query param.
   * `user.branch` puede venir como array (`[15]` o `"[15]"`) y al mandarlo tal
   * cual el backend recibe `[15]` en lugar de `15`. Se prioriza la sucursal
   * activa del header (`selectedBranchId`) y, si aún no se inicializó (primer
   * montaje, antes de que MainLayout llame a `init`), se deriva del usuario en
   * sesión con la misma prioridad que `init`.
   */
  const currentBranchId = () => {
    const fromStore = Number(selectedBranchId.value);
    if (Number.isInteger(fromStore) && fromStore > 0) return fromStore;

    const user = session.getUser();
    if (!user) return null;

    const direct = Number(user.branch);
    if (Number.isInteger(direct) && direct > 0) return direct;

    const allowed = parseBranchIds(user.allowed_branches);
    if (allowed.length > 0) return allowed[0];

    const own = parseBranchIds(user.branch);
    return own.length > 0 ? own[0] : null;
  };

  /**
   * Re-hidratación reactiva del contexto de sucursal (RX-4, hallazgo R4).
   * Persiste la sucursal y dispara la re-monta/reactivación de las vistas sin
   * recargar la página: App.vue usa `branchVersion` como `:key` del
   * <router-view>, por lo que las vistas vuelven a ejecutar sus cargas y los
   * catálogos dependientes se re-obtienen con la sucursal activa nueva.
   */
  const switchBranch = (branchId) => {
    setActiveBranch(Number(branchId));
    const active = userBranches.value.find((b) => Number(b.id) === Number(branchId));
    currentBranchName.value = active ? active.name : `Agencia #${branchId}`;
    branchVersion.value += 1;
  };

  return {
    selectedBranchId,
    userBranches,
    currentBranchName,
    isLoadingBranches,
    branchVersion,
    init,
    setBranches,
    setActiveBranch,
    currentBranchId,
    switchBranch,
    parseBranchIds,
  };
}